/* istanbul ignore file */
import React, { createContext, useCallback, useEffect, useContext, useMemo, useState, useRef } from 'react';
import type { ReactNode } from 'react';
import type { SlideOutProps } from '@interstate/components/SlideOut';
import { useDispatch, useSelector } from 'react-redux';
import * as printPdfActionCreator from '@makemydeal/dr-dash-store/actions/printPdfActionCreator';
import * as proposalActionCreators from '@makemydeal/dr-dash-store/actions/proposalActionCreators';
import * as proposalSelectors from '@makemydeal/dr-dash-store/selectors/proposalSelectors';
import { Box } from '@interstate/components/Box';
import { Button } from '@interstate/components/Button';
import { ChatBubbleLeftIcon, EnvelopeIcon } from '@interstate/components/Icons';

// Views managed by the proposal slide out
export type ProposalSlideOutView = 'list' | 'create' | 'edit' | 'preview' | 'view' | 'none';

interface ProposalSlideOutContextValue {
    view: ProposalSlideOutView;
    setView: (view: Exclude<ProposalSlideOutView, 'none'>) => void; // external callers only set list/create/edit
    shown: boolean;
    hide: () => void;
    getTitle: (view?: ProposalSlideOutView) => string;
    getContent: (view?: ProposalSlideOutView) => React.ReactElement | undefined;
    getFooterProps: (view?: ProposalSlideOutView) => SlideOutProps['footer'];
}

const ProposalSlideOutContext = createContext<ProposalSlideOutContextValue | undefined>(undefined);

// Direct imports (no Suspense / lazy loading per requirement)
import ProposalList from './proposalList/ProposalList';
import type { CreateProposalHandle } from './createProposal/CreateUpdateProposal';
import { CreateUpdateProposal } from './createProposal/CreateUpdateProposal';
import type { PreviewProposalHandle } from './previewProposal/PreviewProposal';
import { PreviewProposal } from './previewProposal/PreviewProposal';
import { dealerSelectors } from '@makemydeal/dr-shared-store';
import {
    createAddingProposalVersionEvent,
    createSavingProposalSettingsEvent,
    createSavingAndPrintingProposalEvent,
    createTextPrintingProposalEvent,
    createEmailPrintingProposalEvent,
    createFourSquarePrintLayoutSelectedEvent
} from '../dealTemplates/analytics/cdlEventCreators';
import * as dealTemplateSelectors from '@makemydeal/dr-dash-store/selectors/dealTemplateSelectors';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as printPdfSelectors from '@makemydeal/dr-dash-store/selectors/printPdfSelectors';
import * as quoteSelectors from '@makemydeal/dr-dash-store/selectors/quoteSelectors';
import { useCustomerUI } from '../customerUI/useCustomerUI';
import { logNewRelicPageAction } from '@makemydeal/dr-activities-common';
import { useValidationGuard } from '../hooks/useValidationGuard';

interface ProviderProps {
    children: ReactNode;
    /** Optional initial view, defaults to none */
    initialView?: ProposalSlideOutView;
}

const SHARING_TYPE_TEXT = 'text';
const SHARING_TYPE_EMAIL = 'email';

export const ProposalSlideOutProvider: React.FC<ProviderProps> = ({ children }) => {
    const dispatch = useDispatch();
    const guard = useValidationGuard('exit', { skipValidationKeys: ['vehicle'] });
    const appliedTemplateId = useSelector(dealTemplateSelectors.getAppliedDealTemplateId);
    const isMvLiteEnabled = useSelector(dealerSelectors.isMvLiteEnabled);
    const { showCustomerCard } = useCustomerUI();
    const sharingUrl = useSelector(printPdfSelectors.getSharingUrl);
    const sharingType = useSelector(printPdfSelectors.getSharingType);
    const shopperInfo = useSelector(offerReduxSelectors.getShopperInfo);
    const customerFirstName = shopperInfo.firstName;
    const isQuoteMode = useSelector(quoteSelectors.getQuoteMode);

    const initialView: ProposalSlideOutView = useSelector(proposalSelectors.proposalListDrawerShown) ? 'list' : 'none';
    const createProposalRef = useRef<CreateProposalHandle>(null);
    const previewRef = useRef<PreviewProposalHandle>(null);
    const [isEditMode, setEditMode] = useState(false);
    const [previewActionsDisabled, setPreviewActionsDisabled] = useState(false);

    const createdProposal = useSelector(proposalSelectors.getCreatedProposal);
    const updatedProposal = useSelector(proposalSelectors.getUpdatedProposal);
    const proposal = isEditMode ? updatedProposal : createdProposal;
    const printingProposals = useSelector(proposalSelectors.getProposalList);

    const { id: proposalId } = proposal || {};

    const [view, setView] = useState<ProposalSlideOutView>(initialView);
    const [submitButtonsActive, setSubmitButtonsActive] = useState<{ [key in ProposalSlideOutView]?: boolean }>({
        list: false,
        create: false
    });

    useEffect(() => {
        setView(initialView);
    }, [initialView]);

    const hide = useCallback(() => {
        setView('none');
        dispatch(proposalActionCreators.toggleProposalListDrawer(false));
    }, []);

    const [proposalName, setProposalName] = useState('');
    const [updateProposalId, setUpdateProposalId] = useState('');
    const [isFourSquare, setIsFourSquare] = useState(false);

    const getCustomMessageWithUrl = useCallback((type: string, url: string, firstName: string) => {
        return () => {
            if (type === SHARING_TYPE_TEXT) {
                return `${firstName}, here's your personalized deal link: ${url}`;
            } else {
                return `${firstName}, here's your personalized <a href="${url}" target="_blank" rel="noopener noreferrer" style="text-decoration: underline;">deal link</a>.`;
            }
        };
    }, []);

    useEffect(() => {
        if (sharingUrl && sharingType) {
            const urlSelector = getCustomMessageWithUrl(sharingType, sharingUrl, customerFirstName);
            const defaultTab = sharingType === SHARING_TYPE_TEXT ? SHARING_TYPE_TEXT : SHARING_TYPE_EMAIL;

            showCustomerCard(true, {
                tab: 'inbox',
                defaultTab,
                messageContentSelector: urlSelector,
                waitForOfferSave: false
            });
            dispatch(printPdfActionCreator.clearPdfUrlForSharing());
        }
    }, [sharingUrl, sharingType, customerFirstName, showCustomerCard, dispatch, getCustomMessageWithUrl]);

    const editProposal = useCallback(
        (id: string, name: string) => {
            const target = printingProposals.find((p) => p.id === id);
            const isFourSq = target?.type === 'fourSquare';
            const isViewOnly = isFourSq && !!target?.isDefault;
            setView(isViewOnly ? 'view' : 'edit');
            setProposalName(name);
            setUpdateProposalId(id);
            setIsFourSquare(!!isFourSq);
            dispatch(proposalActionCreators.getProposal(id));
        },
        [printingProposals]
    );

    const rawHandleText = useCallback(() => {
        const analyticsEvent = createTextPrintingProposalEvent(
            {
                templateId: appliedTemplateId
            },
            isMvLiteEnabled
        );
        dispatch(analyticsEvent);
        dispatch(
            printPdfActionCreator.printPdfForText(
                proposalId,
                previewRef.current?.getSelectedScopeIds(),
                previewRef.current?.getScopeGridSelections()
            )
        );

        logNewRelicPageAction('MV:Printing Proposal - Text clicked', {
            proposalAction: 'text',
            proposalId
        });
    }, [appliedTemplateId, isMvLiteEnabled, dispatch, proposalId]);
    const handleText = useMemo(() => guard.wrap(rawHandleText), [guard.wrap, rawHandleText]);

    const rawHandleEmail = useCallback(() => {
        const analyticsEvent = createEmailPrintingProposalEvent(
            {
                templateId: appliedTemplateId
            },
            isMvLiteEnabled
        );
        dispatch(analyticsEvent);
        dispatch(
            printPdfActionCreator.printPdfForEmail(
                proposalId,
                previewRef.current?.getSelectedScopeIds(),
                previewRef.current?.getScopeGridSelections()
            )
        );
        logNewRelicPageAction('MV:Printing Proposal - Email clicked', {
            proposalAction: 'email',
            proposalId
        });
    }, [appliedTemplateId, isMvLiteEnabled, dispatch, proposalId]);
    const handleEmail = useMemo(() => guard.wrap(rawHandleEmail), [guard.wrap, rawHandleEmail]);

    const getTitle = useCallback(
        (targetView: ProposalSlideOutView = view): string => {
            switch (targetView) {
                case 'list':
                    return 'Printing Proposal Settings';
                case 'create':
                    return 'Printing Proposal Settings';
                case 'preview':
                    return 'Printing Proposal Preview';
                case 'edit':
                case 'view':
                    return `Printing Proposal Settings: ${proposalName}`;
                case 'none':
                    return '';
            }
        },
        [view, proposalName]
    );

    const getContent = useCallback(
        (targetView: ProposalSlideOutView = view): React.ReactElement | undefined => {
            switch (targetView) {
                case 'list':
                    return <ProposalList editProposal={editProposal} />;
                case 'create':
                    return (
                        <CreateUpdateProposal
                            ref={createProposalRef}
                            setView={setView}
                            shouldDisableSubmit={(disabled: boolean) => {
                                setSubmitButtonsActive((prev) => ({ ...prev, create: disabled }));
                            }}
                            setEditMode={setEditMode}
                        />
                    );
                case 'preview':
                    return (
                        <PreviewProposal
                            ref={previewRef}
                            isEditMode={isEditMode}
                            isFourSquare={isFourSquare}
                            closeDrawer={hide}
                            onActionsDisabled={setPreviewActionsDisabled}
                        />
                    );
                case 'edit':
                    return (
                        <CreateUpdateProposal
                            ref={createProposalRef}
                            setView={setView}
                            shouldDisableSubmit={(disabled: boolean) => {
                                setSubmitButtonsActive((prev) => ({ ...prev, create: disabled }));
                            }}
                            isEditMode={true}
                            setEditMode={setEditMode}
                            isFourSquare={isFourSquare}
                        />
                    );
                case 'view':
                    return (
                        <CreateUpdateProposal
                            ref={createProposalRef}
                            setView={setView}
                            isEditMode={true}
                            isFourSquare={true}
                            isViewMode={true}
                        />
                    );
                case 'none':
                    return undefined;
            }
        },
        [view, isEditMode, proposalName, proposalId, isFourSquare, printingProposals]
    );

    const getFooterProps = useCallback(
        (targetView: ProposalSlideOutView = view): SlideOutProps['footer'] => {
            switch (targetView) {
                case 'create':
                    return {
                        primary: {
                            label: 'Save and Preview',
                            disabled: submitButtonsActive.create,
                            action: () => {
                                if (createProposalRef.current) {
                                    const formData = createProposalRef.current.getFormData();

                                    // Dispatch the CREATE_PROPOSAL action with the form data
                                    dispatch(proposalActionCreators.createProposal(formData));
                                    const analyticsEvent = createSavingProposalSettingsEvent(
                                        {
                                            templateId: appliedTemplateId
                                        },
                                        isMvLiteEnabled
                                    );
                                    dispatch(analyticsEvent);
                                }
                            }
                        },
                        options: {
                            buttonStyle: 'tertiary',
                            label: 'Back',
                            action: () => setView('list')
                        }
                    };
                case 'list': {
                    const addPrintingProposal = {
                        buttonStyle: 'secondary' as const,
                        label: 'Add printing proposal',
                        action: () => {
                            const analyticsEvent = createAddingProposalVersionEvent(
                                {
                                    templateId: appliedTemplateId
                                },
                                isMvLiteEnabled
                            );
                            dispatch(analyticsEvent);
                            setView('create');
                        }
                    };
                    return { primary: addPrintingProposal };
                }
                case 'preview':
                    return {
                        footerComponent: (
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    gap: 2,
                                    backgroundColor: '#f5f5f5',
                                    padding: 2
                                }}
                            >
                                <Button
                                    buttonStyle="tertiary"
                                    onClick={() => {
                                        if (proposal?.id) {
                                            editProposal(proposal.id, proposal?.title || '');
                                        } else {
                                            setView('list');
                                        }
                                    }}
                                    data-testid="back-button"
                                >
                                    Back
                                </Button>
                                <Button
                                    buttonStyle="secondary"
                                    onClick={handleText}
                                    data-testid="text-button"
                                    startIcon={<ChatBubbleLeftIcon />}
                                    disabled={previewActionsDisabled || isQuoteMode}
                                >
                                    Text
                                </Button>
                                <Button
                                    buttonStyle="secondary"
                                    onClick={handleEmail}
                                    data-testid="email-button"
                                    startIcon={<EnvelopeIcon />}
                                    disabled={previewActionsDisabled || isQuoteMode}
                                >
                                    Email
                                </Button>
                                <Button
                                    buttonStyle="primary"
                                    disabled={previewActionsDisabled}
                                    onClick={() => {
                                        const analyticsEvent = isFourSquare
                                            ? createFourSquarePrintLayoutSelectedEvent(
                                                  { proposalId, templateId: appliedTemplateId },
                                                  isMvLiteEnabled
                                              )
                                            : createSavingAndPrintingProposalEvent(
                                                  { templateId: appliedTemplateId },
                                                  isMvLiteEnabled
                                              );
                                        dispatch(analyticsEvent);
                                        dispatch(
                                            printPdfActionCreator.printCustomizablePdf(
                                                proposalId,
                                                previewRef.current?.getSelectedScopeIds(),
                                                previewRef.current?.getScopeGridSelections()
                                            )
                                        );

                                        logNewRelicPageAction('MV:Printing Proposal - Print clicked', {
                                            proposalAction: 'print',
                                            proposalId
                                        });
                                    }}
                                    data-testid="print-button"
                                >
                                    Print
                                </Button>
                            </Box>
                        )
                    };
                case 'edit':
                    return {
                        primary: {
                            label: 'Save and Preview',
                            disabled: submitButtonsActive.create,
                            action: () => {
                                if (createProposalRef.current) {
                                    const formData = createProposalRef.current.getFormData();
                                    dispatch(proposalActionCreators.updateProposal(formData, updateProposalId));
                                    const analyticsEvent = createSavingProposalSettingsEvent(
                                        {
                                            templateId: appliedTemplateId
                                        },
                                        isMvLiteEnabled
                                    );
                                    dispatch(analyticsEvent);
                                }
                            }
                        },
                        options: {
                            buttonStyle: 'tertiary',
                            label: 'Back',
                            action: () => setView('list')
                        }
                    };
                case 'view':
                    return {
                        primary: {
                            label: 'Back',
                            buttonStyle: 'secondary',
                            action: () => setView('list')
                        }
                    };
                case 'none':
                    return undefined;
            }
        },
        [view, submitButtonsActive, isEditMode, proposalId, proposal, previewActionsDisabled, isQuoteMode]
    );

    const value: ProposalSlideOutContextValue = useMemo(
        () => ({
            view,
            setView,
            shown: view !== 'none',
            hide,
            getTitle,
            getContent,
            getFooterProps
        }),
        [view, setView, hide, getTitle, getContent, getFooterProps]
    );

    return <ProposalSlideOutContext.Provider value={value}>{children}</ProposalSlideOutContext.Provider>;
};

export const useProposalSlideOutContext = () => {
    const ctx = useContext(ProposalSlideOutContext);
    if (!ctx) {
        throw new Error('useProposalSlideOutContext must be used within a ProposalSlideOutProvider');
    }
    return ctx;
};

export default ProposalSlideOutProvider;
